import type {
    ActorEvent,
    ButtonItem,
    HighLightConditions,
    RecordLikeProtocol,
    RichTextContentProtocol,
    TableRowHeightProtocol,
    TableViewOptions,
    ViewField
} from '@lighthouse/core'
import { conversionHighLightRecord } from '@lighthouse/shared'
import { useVirtualizer } from '@tanstack/react-virtual'
import chroma from 'chroma-js'
import { add } from 'rambda'
import React, { useEffect, useMemo, useTransition } from 'react'
import styled from 'styled-components'

import { useViewBlockContext } from '../../context'
import TableRecord from './TableRecord'
import { getAvatarWidth } from './utils'

interface TableContentProps {
    recordOperatorActions?: ButtonItem[]
    viewFields: ViewField[]
    checkable?: boolean
    selectedRecords?: string[]
    isMobile?: boolean
    highLightRules?: HighLightConditions
    blockWidth: number
    scrollRef: React.RefObject<HTMLDivElement>
    tableRowHeight?: TableRowHeightProtocol
    designConfig: TableViewOptions
    onRecordSelect?: (recordIds: string[]) => void
    onRecordOperatorActionTrigger?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record: RecordLikeProtocol) => Promise<void>
    recordClickable?: boolean
    onRenderButtonTitle: (v: RichTextContentProtocol, record?: RecordLikeProtocol) => string
}

const SCxTableContent = styled.div`
    min-width: 100%;
    width: fit-content;
`

const SCxRemain = styled.div`
    width: 8px;
    height: 100%;
`

export const TableContent: React.FC<TableContentProps> = ({
    recordOperatorActions,
    viewFields,
    checkable,
    selectedRecords = [],
    isMobile,
    highLightRules = [],
    blockWidth,
    scrollRef,
    tableRowHeight,
    designConfig,
    onRecordSelect,
    onRecordOperatorActionTrigger,
    onRecordClick,
    onRenderButtonTitle,
    recordClickable
}) => {
    const { dataSource, recordPoints } = useViewBlockContext()
    const { schema } = dataSource
    const { isShowListAvatar, listAvatar } = designConfig
    const [_, startTransition] = useTransition()
    const { cell: cellStyleConditions, row: rowStyleConditions } = useMemo(
        () => conversionHighLightRecord(highLightRules),
        [highLightRules]
    )

    const overscan = useMemo(() => {
        let width = blockWidth
        return viewFields.reduce<number>((prev, cur) => {
            if (width > 0) {
                width -= cur.width || 160
                return ++prev
            }
            return prev
        }, 0)
    }, [blockWidth, viewFields])
    const isAvatar = useMemo(() => isShowListAvatar && listAvatar && schema?.[listAvatar], [isShowListAvatar, listAvatar, schema])

    const columnPaddingStart = useMemo(() => {
        const avatarWidth = getAvatarWidth(isMobile)
        if (isMobile) {
            const width = isAvatar ? avatarWidth : 0
            return add(8, width)
        }
        const width = isAvatar ? avatarWidth : 0
        return add(68, width)
    }, [isAvatar, isMobile])

    const realViewFields = useMemo(() => {
        let fields: ViewField[] = viewFields
        if (isMobile) {
            fields = fields.slice(1)
        }
        if (isAvatar) {
            fields = fields.filter(item => item.fieldId !== listAvatar)
        }
        return fields
    }, [isAvatar, isMobile, listAvatar, viewFields])

    const columnVirtualizer = useVirtualizer({
        horizontal: true,
        count: realViewFields.length,
        getScrollElement: () => scrollRef.current,
        paddingStart: columnPaddingStart,
        paddingEnd: 44,
        estimateSize: index => realViewFields?.[index].width || 160,
        overscan
    })

    useEffect(() => {
        startTransition(() => {
            columnVirtualizer.measure()
        })
    }, [columnVirtualizer, realViewFields, blockWidth])

    const documentStyles = getComputedStyle(document.body)
    const themeColor = documentStyles.getPropertyValue('--color-app-main').trim()
    const grayColor = documentStyles.getPropertyValue('--color-gray-50').trim()
    const hoverColor = chroma(themeColor).alpha(0.06)?.hex()
    const mixColor = chroma(themeColor).alpha(0.1).hex()
    const stripeHoverColor = chroma.mix(mixColor, grayColor).alpha(0.3).hex()

    const rowHeight = useMemo(() => {
        if (isMobile) {
            return 78
        }
        switch (tableRowHeight) {
            case 'small': {
                return 38
            }
            case 'middle': {
                return 53
            }
            case 'large': {
                return 57
            }
            default: {
                return 45
            }
        }
    }, [isMobile, tableRowHeight])

    return (
        <SCxTableContent
            style={{
                width: `${columnVirtualizer.getTotalSize()}px`,
                position: 'relative'
            }}
        >
            {recordPoints.map((point, index) => {
                return (
                    <TableRecord
                        key={point.id}
                        recordOperatorActions={recordOperatorActions}
                        index={index}
                        id={point.id}
                        rowHeight={rowHeight}
                        rowWidth={columnVirtualizer.getTotalSize()}
                        virtualItems={columnVirtualizer.getVirtualItems()}
                        checkable={checkable}
                        selectedRecords={selectedRecords}
                        isMobile={isMobile}
                        cellStyleConditions={cellStyleConditions}
                        rowStyleConditions={rowStyleConditions}
                        viewFields={realViewFields}
                        primaryFieldId={viewFields?.[0]?.fieldId}
                        scrollRef={scrollRef}
                        blockWidth={blockWidth}
                        designConfig={designConfig}
                        hoverColor={hoverColor}
                        stripeHoverColor={stripeHoverColor}
                        onRecordSelect={onRecordSelect}
                        onRecordOperatorActionTrigger={onRecordOperatorActionTrigger}
                        onRecordClick={onRecordClick}
                        onRenderButtonTitle={onRenderButtonTitle}
                        recordClickable={recordClickable}
                    />
                )
            })}
            <SCxRemain />
        </SCxTableContent>
    )
}
